import { useRef, useState } from 'react';

import { ApproverMessage } from '../../../../../types/ApproverMessage';
import { Button } from '../../../../cdl/Button/Button';
import { useDrawer } from '../../../../cdl/Drawer/useDrawer.hook';
import { useDrawerGuard } from '../../../../cdl/Drawer/useDrawerGuard';
import { ButtonGroup } from '../../../../common/buttons/ButtonGroup';
import { translate } from '../../../../common/helpers/translate.helper';
import { Box } from '../../../../common/ui/Box';
import { useOfferApprovalSettings } from '../../../../offer/MultipleApprovers/hooks/useOfferApprovalSettings';
import { ApproverCheckboxGroup } from '../../components/intelligence/ApproverCheckboxGroup';

interface OfferApprovalSectionProps {
    selectedOfferId: string;
    onRequestApproval: (selectedApproverEmails: string[], message?: string, selectedOfferId?: string) => Promise<void>;
    message?: string;
}

export const OfferApprovalSection = ({ selectedOfferId, onRequestApproval, message }: OfferApprovalSectionProps) => {
    const { pop, layerId, requestClose } = useDrawer();

    const offerApprovalSettingsQuery = useOfferApprovalSettings(selectedOfferId, true);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedApproverEmails, setSelectedApproverEmails] = useState<string[]>(() => {
        const offerApprovalSettings = offerApprovalSettingsQuery.data;
        if (!offerApprovalSettings) return [];

        const hasOnlyOneApprover = offerApprovalSettings.suggestedApprovers.length === 1;
        const hasAtLeastOneApprover = offerApprovalSettings.suggestedApprovers.length >= 1;

        if (hasOnlyOneApprover || (hasAtLeastOneApprover && offerApprovalSettings.preventMultipleApprovers)) {
            return [offerApprovalSettings.suggestedApprovers[0].emailAddress];
        }
        return [];
    });

    const initialSelection = useRef(selectedApproverEmails);
    const hasUnsavedChanges =
        selectedApproverEmails.length !== initialSelection.current.length ||
        selectedApproverEmails.some((email) => !initialSelection.current.includes(email));
    useDrawerGuard({ hasUnsavedChanges, isBusy: isLoading });
    const offerApprovalSettings = offerApprovalSettingsQuery.data;

    const handleApproverChange = (approver: ApproverMessage) => {
        if (offerApprovalSettings?.preventMultipleApprovers) {
            setSelectedApproverEmails([approver.emailAddress]);
        } else {
            setSelectedApproverEmails((prev) => {
                if (prev.includes(approver.emailAddress)) {
                    return prev.filter((email) => email !== approver.emailAddress);
                } else {
                    return [...prev, approver.emailAddress];
                }
            });
        }
    };

    const handleRequestApproval = async () => {
        setIsLoading(true);
        try {
            await onRequestApproval(selectedApproverEmails, message, selectedOfferId);
            pop(layerId);
        } catch {
            setIsLoading(false);
        }
    };

    const isRequestApprovalDisabled = selectedApproverEmails.length === 0;

    if (!offerApprovalSettings || !offerApprovalSettings.requiresApproval) {
        return null;
    }

    return (
        <Box padding={24} display="grid" gap={16} boxShadow="large">
            <ApproverCheckboxGroup
                offerApprovalSettings={offerApprovalSettings}
                selectedApproverEmails={selectedApproverEmails}
                onApproverChange={handleApproverChange}
            />

            <ButtonGroup justifyContent="flex-end">
                <Button onClick={() => requestClose()} disabled={isLoading}>
                    {translate('btn.cancel')}
                </Button>
                <Button
                    emphasis="high"
                    onClick={handleRequestApproval}
                    disabled={isRequestApprovalDisabled}
                    isLoading={isLoading}
                >
                    {translate('closelinkInsights.offerReview.requestApproval')}
                </Button>
            </ButtonGroup>
        </Box>
    );
};
